<script lang="ts">
	import { createForm } from 'felte';
	import { validator } from '@felte/validator-zod';
	import { z } from 'zod';
	import { direction } from '$lib/stores/directionStore';
	import Button from '$lib/components/Button/Button.svelte';
	import ButtonLink from '$lib/components/ButtonLink/ButtonLink.svelte';
	import { _ } from 'svelte-i18n';
	import { getHubStores } from '$lib/stores/hubStores';
	import { page } from '$app/stores';
	import { isLocalDevelopment } from '$lib/utils/environmentResolver';
	import ErrorList from '$lib/components/Error/ErrorList.svelte';
	import { email } from './emailStore';
	import { instanceOfWebRPCError } from '$lib/utils/getErrorMessage';
	import Input from '$lib/components/Inputs/Input.svelte';

	const { hub, api, pod } = getHubStores();

	export let isResetPasswordFormDisplayed: boolean;
	export let isJoinCommunityFormDisplayed: boolean;
	export let isSparqAdminInfoModalDisplayed: boolean;

	const isSsoLoginEnabled = $hub.sso_buttons?.length;
	const isEmailLoginEnabled = !$hub.settings.auth_options.disable_email_form;
	const isJoinCommunityLoginEnabled = $hub.settings.enable_join_now;
	let passwordStep = false;
	let serverErrors: string[] = [];

	const emailSchema = z.object({
		email: z.string().email(),
	});
	const { errors: emailErrors, form: emailForm } = createForm<z.infer<typeof emailSchema>>({
		extend: validator({ schema: emailSchema }),
		onSubmit: async (values) => {
			serverErrors = [];
			try {
				const resp = await api.lookupHubUser({ hubId: $hub.id, email: values.email });

				if (resp.lookupHubUser.redirect_url) {
					window.location.assign(resp.lookupHubUser.redirect_url);
					return;
				}

				if (resp.lookupHubUser.action === 'third-party-idp') {
					isSparqAdminInfoModalDisplayed = true;
					return;
				}

				$email = values.email;
				passwordStep = true;
			} catch (error) {
				serverErrors = [$_('session.server.general.error')];
			}
		},
	});

	const passwordSchema = z.object({
		password: z.string(),
	});

	const { errors: passwordErrors, form: passwordForm } = createForm<z.infer<typeof passwordSchema>>(
		{
			extend: validator({ schema: passwordSchema }),
			onSubmit: async (values) => {
				serverErrors = [];

				try {
					const { response } = await api.login({
						hubID: $hub.id,
						email: $email,
						password: values.password,
					});
					if (isLocalDevelopment(pod)) {
						document.cookie = `jwt=${response.jwt}`;
						window.location.assign(`/${$page.params.hubPrefix}`);
					} else {
						window.location.reload();
					}
				} catch (error) {
					if (instanceOfWebRPCError(error)) {
						switch (error.status) {
							case 403:
								serverErrors = [$_('session.sign.in.error')];
								return;
							case 400:
								serverErrors = [$_('session.sign.in.error.too.many.attempts')];
								return;
							default:
								serverErrors = [$_('session.server.general.error')];
						}
					}
				}
			},
		}
	);

	const joinCommunity = () => {
		window.location.assign($hub.settings.join_now_url);
	};
</script>

<div
	class="loginOptions {$direction === 'RTL' ? 'RTL' : ''}{!isSsoLoginEnabled ||
	(!isEmailLoginEnabled && !isJoinCommunityLoginEnabled)
		? 'narrowWidth'
		: ''} ">
	{#if isEmailLoginEnabled || isJoinCommunityLoginEnabled}
		<div class="leftContainer">
			{#if isJoinCommunityFormDisplayed}
				<div class="joinCommunity">
					<Button on:click={joinCommunity} disabled={!$hub.settings.join_now_url}
						>{$_('login.join.now')}</Button>
					<div class="buttonLink">
						{$_('session.email.already.member')}
						<ButtonLink on:click={() => (isJoinCommunityFormDisplayed = false)} color="#0070a8">
							{$_('session.email.signin')}
						</ButtonLink>
					</div>
				</div>
			{:else}
				<div class="loginForm">
					{#if isEmailLoginEnabled}
						{#if passwordStep}
							<ErrorList errors={serverErrors} />
							<h2 class="passwordHint">
								{@html $_('session.hub.password.instruction.svelte', {
									values: { hubName: $hub.name },
								})}
							</h2>

							<div id="emailInfo">
								<span>{$email}</span>
								<button
									type="button"
									on:click={() => {
										passwordStep = false;
										serverErrors = [];
									}}>
									{$_('common.change')}
								</button>
							</div>
							<form use:passwordForm>
								<Input
									type="password"
									name="password"
									label={$_('common.password')}
									autofocus
									invalid={$passwordErrors.password?.[0]} />
								<Button type="submit" backgroundColor="#0070a8">{$_('common.continue')}</Button>
							</form>
						{:else}
							<ErrorList errors={serverErrors} />

							<div class="emailHint">
								{$_('session.hub.email.instruction')}
							</div>
							<form use:emailForm>
								<Input
									name="email"
									type="email"
									value={$email}
									label={$_('common.email')}
									invalid={$emailErrors.email?.[0]} />
								<Button type="submit" backgroundColor="#0070a8">{$_('common.continue')}</Button>
							</form>
						{/if}

						<div class="leftContainerFooter">
							<div id="troubleSignIn" class="buttonLink">
								<ButtonLink on:click={() => (isResetPasswordFormDisplayed = true)} color="#0070a8">
									{$_('session.having.trouble.signing.in')}
								</ButtonLink>
							</div>
							{#if isJoinCommunityLoginEnabled}
								<div class="buttonLink">
									{$_('session.email.not.member')}
									<ButtonLink
										on:click={() => {
											isJoinCommunityFormDisplayed = true;
											serverErrors = [];
										}}
										color="#0070a8">
										{$_('session.email.join')}
									</ButtonLink>
								</div>
							{/if}
						</div>
					{:else if isJoinCommunityLoginEnabled}
						<div class="joinCommunity">
							<Button
								on:click={joinCommunity}
								disabled={!$hub.settings.join_now_url}
								backgroundColor="#0070a8">{$_('login.join.now')}</Button>
						</div>
					{/if}
				</div>
			{/if}
		</div>
	{/if}
	{#if (isEmailLoginEnabled || isJoinCommunityLoginEnabled) && isSsoLoginEnabled}
		<div class="separator">
			<span class="or">{$_('common.or')}</span>
		</div>
	{/if}
	{#if isSsoLoginEnabled && $hub.sso_buttons?.length}
		<div class="rightContainer scroll">
			<div class="ssoWrapper">
				{#each $hub.sso_buttons as ssoButton}
					<a
						href={ssoButton.url}
						id={ssoButton.provider_type}
						style={ssoButton.bg_color ? `background-color: ${String(ssoButton.bg_color)}` : ''}>
						{ssoButton.title}
					</a>
				{/each}
			</div>
		</div>
	{/if}
</div>

<style>
	form > :global(button) {
		inline-size: 100%;
		margin-block: var(--gutterSmall);
	}

	.joinCommunity {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		block-size: 100%;
	}

	.loginForm {
		flex-grow: 1;
		inline-size: 100%;
		margin: auto;
	}

	.leftContainerFooter {
		inline-size: 100%;
	}

	.loginOptions {
		display: flex;
		min-block-size: 280px;
		inline-size: 700px;
	}

	.narrowWidth {
		inline-size: 300px !important;
	}

	.leftContainer {
		display: flex;
		flex: 1;
		flex-direction: column;
	}

	.separator {
		display: flex;
		position: relative;
		align-items: center;
		justify-content: center;
		padding: 40px;
		text-transform: uppercase;
	}

	.or {
		position: relative;
		padding: 10px;
		background-color: #fff;
		color: #1a1a1a;
		font-size: var(--fontSizeSmall);
		font-weight: 500;
	}

	.separator::before {
		content: '';
		display: block;
		position: absolute;
		block-size: 100%;
		inline-size: 2px;
		background-color: #bbb;
	}

	.emailHint {
		display: block;
		margin-block: 25px;
		margin-inline: 0;
		color: var(--BLACK);
		font-size: var(--fontSizeNormal);
		font-weight: 500;
		text-align: center;
	}

	.passwordHint {
		color: var(--BLACK);
		text-align: center;
	}

	:global(.passwordHint span) {
		font-weight: 700;
	}

	.buttonLink {
		font-size: var(--fontSizeSmaller);
		text-align: center;
	}

	#troubleSignIn {
		margin-block: 3px;
		margin-block-start: auto;
		margin-inline: 0;
	}

	.rightContainer {
		flex: 1;
		align-items: center;
	}

	.ssoWrapper a {
		display: block;
		position: relative;
		inline-size: 100%;
		margin-block: var(--gutterMedium);
		margin-inline: 0;
		padding-block: 11px;
		padding-inline: 0;
		transition: background-color 200ms ease-out 0s, box-shadow 200ms ease-out 0s;
		border: 1px solid var(--normalColor);
		color: white;
		font-size: var(--fontSizeSmaller);
		font-weight: 500;
		text-align: center;
	}

	.ssoWrapper a:hover,
	.ssoWrapper a:focus {
		text-decoration: none;
	}

	.ssoWrapper {
		margin-inline-end: var(--gutterSmall);
	}

	#facebook,
	#google,
	#linkedin,
	#azure,
	#onelogin,
	#oidc,
	#facebookRTL,
	#googleRTL,
	#linkedinRTL,
	#azureRTL,
	#oneloginRTL,
	#oidcRTL {
		background-color: var(--primaryAccentColor);
	}

	#facebook::before,
	#google::before,
	#linkedin::before,
	#facebookRTL::before,
	#googleRTL::before,
	#linkedinRTL::before {
		content: ' ';
		position: absolute;
		block-size: 24px;
		inline-size: 24px;
		background-size: contain;
	}

	#facebook::before,
	#facebookRTL::before {
		margin-block-start: -3px;
		margin-inline-start: -28px;
		background-image: url('data:image/png;base64,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');
	}

	#facebookRTL::before {
		margin-inline: 0 -28px;
	}

	#facebook:hover,
	#facebook:focus,
	#facebookRTL:hover,
	#facebookRTL:focus {
		background-color: rgb(24 119 242 / 90%);
	}

	#google,
	#googleRTL {
		color: rgb(116 116 116);
	}

	#google:hover,
	#google:focus,
	#googleRTL:hover,
	#googleRTL:focus {
		background-color: rgb(244 244 244 / 60%);
	}

	#google::before,
	#googleRTL::before {
		margin-block-start: -3px;
		margin-inline-start: -35px;
		background-image: url('data:image/png;base64,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');
	}

	#googleRTL::before {
		margin-inline: 0 -35px;
	}

	#linkedin::before,
	#linkedinRTL::before {
		margin-block-start: -3px;
		margin-inline-start: -30px;
		background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAHEAAABgCAYAAAAq9J3uAAAACXBIWXMAACxLAAAsSwGlPZapAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAlLSURBVHgB7Z17cFTVHce/5+zdzeb9QhICxJAJSXiEl2JRdATbEaEQHsOjdpD6gASlZUZtB9syiE6daf9oqVUqr1osUFsYC4YI46BDUB4tVMyQGPICApEkZCFPsrvZvfeenrsVSCQie/dmd7mez8yyd8+9l0nyuef1O48l6EHGk7sT7BGWpQQsnzEyCoTZIAgURkDq+dsHKsX2qrdmn4LBkGsH2YV7Z1Com3jSYAj6D0JeqVQbXsOmQi8Mgmr/5C7bM42CvSsEBgGVrcmlaa/6MqhBkNwVHyVDcZaAYTQEQYJ5Kej0io2zPoYBUOZxPSYEBhtiVcAKsHYthQFQSjEfgqDD7T06pmnMABgAZWAjIAg6DEhwybZkGADl7VMLBCFBYh5D/vaGlMmC0CIkmgAh0QQIiSZASDQBEgwg2m7FxOwkTBs/CFmDYxEhWXC53Y3Sc6348GQjqr7sgKD/CEgi4dG/7+UMwKr5o5A7JK7XueFpsbh/xF1Y8kgmdh+rx/riarR1eSAwHt3FqRa9XfRgBjavnHSTwJ5E2yUsnjoM237+AFITIiEwHt0Svz82FS8tHAmJ3l4wPmtQLN766X2wWgwL3gu+QpdEq0SxYlYOIqz+BRy0HDtvcjoExqJL4sOjU5DNGzB6WDotCzG8ISQwDl0SfzAuBZToKxYHJ0chZ6i+B0DQN7okZqfFQS8kwPsFN6NLYkxUYN1Lu1UMnBiJLomO9m4EgugvGosuiWV1bdCLR1Zxur4dAuPQJfGDExehMAY9nKprRW1jJwTGoUuiFgs9WHoJevjLh7WQFX0PgKBvdEnUisTX/lmGxlaXX/e9d+QCSsqaITAW3WG3pjY3fvL7o6i5eHtF4zsfn8XLOwyfwS5AgKMY9ZedeOZPx7BiZg4e4CMWQwdE9TqvFZtlvA7cUVKHj0oboaiiGO0PAh5P1Loba3kOi4+yIW9YvC/QHWmT0NzmQvn5dpxp6hR1YD9jyKCwRrvTg8NfOHwvQXAR0zNMgJBoAoREE2BYnXgnQClBfLQNqQl2pCbaERP5/3FNd7eMDpcXzbyR1tblRTuP7ap3UEtat8S8uxMQHen/7dU82tNy9ZsD4Now5bjMJERY/Sskmtu6cbbp5j4r4eKGJkfi4bxUTB2TgpwhcUiK6XsVuxZJvNzhRkOLCzUNnfikvBlHTzvQ5ZYRzuiWuDI/Bw+OGgh/WfbGv2/ZgrVJFGseH43cIfHwh33/bcCLWz7rlRajTdJ6ZBieeTTLd/xtaA/QXfF232vssETMn5zuCzFuO3gOe47Vh20/17TF6X05yXj5x2OQmRKDQNBy7m+eGIs59w/Fr98pxQVHF8INUzZs8icNweafTQpYYE/uzUrCrl8+hAmZiQg3TCdxxr1pWL0oz1csG01clBW/fXoCkuPsCCdMJXFkejx+tWg0YiP7r5bQ4sN/WDoBEbbwmWJiGolaa/bFuSOQHBuB/mZidjKmT0hDuGAaidqMdG0kJRhoM/aen5vrW6IQDoiIjU4G8m7IuDBp5AiJATCXdzvCASExALS6MTYy9EsSvhMSu2UVVzq7eWjO7Xt3exUYQSIP3+UODf1sdlMHwLX4pxYuO8Ljn9oMBLdH8S2tG8gD4KN57LfgsSxkBBAQsFooxvHw3InqKwglppSoBbI37q/Bm8VVfcY7251en+ADnzf6+pWB1G2Zg4yLCunFlMXpuj2nsf4bBPbkKh+deOXvp3CgtBF6yUgREg3n0woHtpecg3ybIw7dXtW3n4Cqc0b7oMTQL2E3ncR/HTkPV7d/DRdtuOlEdQv0EIwI0bdhKomtfLD5RI0+GYe/0LcsQeINpVAv1TOVxPLzbbjSoW/ZXdn5dt1FaoQttH9GU0msuKB/yVxTi8vvYvgaWlcjlJhKojY3Ri+dbi88vJGjB0JCu62LqSQ6WvWve9RaqbLOOTQWRf/DYwSmkuiW9f86XkXVvXA21IgAuAkQEk2AkGgChEQTICSaACHRBAiJBkCoiNjc8SgktCMZQqIBqCEOEgiJJkBINAFCogkQEk2AkGgChEQTICSaACHRBAiJJkBINAFCogkQEk2AkGgChEQToHuR6aqtn/v2/faX5nb3Lc9rk3gL3zyO6Aj/f7SGFif0os3+fmrdURD4P5tbW7QaSnRLbOn0+F79gba2PhTUXQq/zfdynj4cCzhjFdpJ4YrsqN0x46Zv2f5ObVp7J5FZsCfPBlIA2prOFOawWKwexMixucuLKAV5vWLDrOPXrhV1YhjCRc20gvxNsVje/1HqZ3OrNucvrdqQ/5xik5ZSmW5QGf6YU1i8GGC+sl9IDDNGFe4dC4YXrJI9P05JPbS1buTAjCd3pmovoBYVW2Z+KtssUwnBshGFxdO1e4TEMGLK2oOSArZetklLyv88rf4qaZwdabOftkfY37Xb7f+QujPKRzxbNKf2jRndXngWMLDVec8WJwqJYURDc+d4XkRe4pK+vJbGC8zSyo35Uys3zJqqqM5Vstv5gpZ+ZsO8ZgYckZk8ReJX8WPxNUChgEj23qtavZgISkp6JnGJUdkFRQMU125KVCkDFtzYlECR94NaJku8W3SROxwOQbDpkonSa306oSyJZ6e6Xmm8ncPrvxJLtJTMz1WoqmfFjeupQ6U0mUJhn0AQdHh9dlK1Wi73TOOyLhHChnztwpOVLe6xTMVRfry/etP8yuvXg6RTsPOU90E28s/imymDjYqtWgOlV5oiHeLl55QpUw727r/vWqgoXvoLnisfH1lQlH7jv6BzZEUto1Vvz27gT8UmnlUD+8pugT8c9JLu976eWLn5h9WMwdE0vHOy9plBcfI6sVY7rvnrzLNc2+vMwpZpn7OfK5oIwtIU4jlxPVCYu/z9tYzR1QQsfHYoNxvadH9CDnid8uIz2+b1WfppOY0Rsp1ZlJdSK0qP12XcLdVtfcr91f0ka+V+W5QLd3ksyk6FKc/XbJrzn+sSFyzYaSlPsk1jxLKSMXYPPxHHC13RBQkU5luo4eatkGr+XpwY6frdsXULb7ndxsjl+8YzyK/yrHhYpnSX1+VoQUICIjq8KUyyP0QJe0JWyZrazfmHtOv7DNlnL987GJSlWTwQuTJAiERURWaOqi2z6vin2+7L3VNQFNXFlIU8Uy3kDZ5onqRtcdzOc9U+ydn29qltS65H6/8HK/EnStamxa4AAAAASUVORK5CYII=');
		background-repeat: no-repeat;
	}

	#linkedinRTL::before {
		margin-inline: 0 -30px;
	}

	#linkedin:hover,
	#linkedin:focus,
	#linkedinRTL:hover,
	#linkedinRTL:focus {
		background-color: rgb(0 123 182 / 80%);
	}

	#azure::before,
	#onelogin::before,
	#oidc::before,
	#azureRTL::before,
	#oneloginRTL::before,
	#oidcRTL::before {
		content: ' ';
		position: absolute;
		block-size: 24px;
		inline-size: 24px;
		margin-block-start: -3px;
		margin-inline-start: -30px;
		background-repeat: no-repeat;
		background-size: contain;
	}

	#azureRTL::before,
	#oneloginRTL::before,
	#oidcRTL::before {
		margin-inline-end: -30px;
	}

	#azure::before,
	#azureRTL::before {
		background-image: url('data:image/png;base64,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');
	}

	#onelogin::before,
	#oneloginRTL::before {
		background-image: url('data:image/png;base64,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');
	}

	#azure:hover,
	#azure:focus,
	#onelogin:hover,
	#onelogin:focus,
	#oidc:hover,
	#oidc:focus,
	#azureRTL:hover,
	#azureRTL:focus,
	#oneloginRTL:hover,
	#oneloginRTL:focus,
	#oidcRTL:hover,
	#oidcRTL:focus {
		background-color: var(--darkenedPrimaryAccentColor);
	}

	#emailInfo {
		margin-block: 35px 20px;
		margin-inline: 0;
		font-size: 12px;
		text-align: center;
	}

	#emailInfo button {
		border: none;
		outline: none;
		background: none;
		color: var(--BLUE);
		cursor: pointer;
	}

	#emailInfo button:hover,
	#emailInfo button:focus {
		text-decoration: underline;
	}

	@media screen and (max-width: 899px) {
		.loginOptions {
			flex-direction: column;
			inline-size: 300px;
		}

		.separator::before {
			block-size: 2px;
			inline-size: 100%;
		}

		.ssoWrapper {
			margin: 0;
		}
	}

	@media screen and (min-width: 900px) {
		.rightContainer {
			max-block-size: 300px;
			overflow: hidden auto;
		}
	}
</style>
